import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, X, TimerOff, Info } from "lucide-react";
import { motion } from "framer-motion";
import AxiosInstance from "@/utils/AxiosInstance";
import { toast } from "sonner";

const READING_TIMEOUT_SECONDS = 4 * 60; // 4 minutes
const POLL_INTERVAL_MS = 3000;

/** Soft blue circle with male/female plug halves.
 * - Default: loops the approach/separate animation (assigning).
 * - `attachPlug`: animates once until fully connected, then stays connected.
 */
function ConnectingPlugsIcon({ attachPlug = false }: { attachPlug?: boolean }) {
  return (
    <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary/15">
      <svg
        viewBox="0 0 24 24"
        className="h-10 w-10 overflow-visible text-primary"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {/* Female socket (top-right) — when attachPlug, slide into the male plug */}
        <motion.g
          initial={attachPlug ? { x: 3, y: -3 } : { x: 2, y: -2 }}
          animate={
            attachPlug
              ? { x: -2.0, y: 2.0 }
              : { x: [2, 0.4, 2], y: [-2, -0.4, -2] }
          }
          transition={
            attachPlug
              ? { duration: 0.8, ease: "easeOut" }
              : { duration: 1.25, repeat: Infinity, ease: "easeInOut" }
          }
        >
          <path d="m19 5 3-3" />
          <path d="m12 6 6 6 2.3-2.3a2.4 2.4 0 0 0 0-3.4l-2.6-2.6a2.4 2.4 0 0 0-3.4 0Z" />
        </motion.g>

        {/* Male plug (bottom-left) — when attachPlug, slide into the female socket */}
        <motion.g
          initial={attachPlug ? { x: -3, y: 3 } : { x: -2, y: 2 }}
          animate={
            attachPlug
              ? { x: 2.0, y: -2.0 }
              : { x: [-2, -0.4, -2], y: [2, 0.4, 2] }
          }
          transition={
            attachPlug
              ? { duration: 0.8, ease: "easeOut" }
              : { duration: 1.25, repeat: Infinity, ease: "easeInOut" }
          }
        >
          <path d="m2 22 3-3" />
          <path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z" />
          <path d="M7.5 13.5 10 11" />
          <path d="M10.5 16.5 13 14" />
        </motion.g>
      </svg>
    </div>
  );
}

interface TakeReadingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  deviceType: string;
  onReadingReceived?: () => void;
  deviceId?: string | null;
  /** Admin flow: caretaker whose gateway/device the reading is assigned from. */
  caretakerId?: string | null;
}

function formatCountdown(totalSeconds: number) {
  const mins = Math.floor(Math.max(0, totalSeconds) / 60);
  const secs = Math.max(0, totalSeconds) % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

const TakeReadingPopup: React.FC<TakeReadingPopupProps> = ({
  isOpen,
  onClose,
  patientId,
  deviceType,
  onReadingReceived,
  deviceId: propDeviceId,
  caretakerId,
}) => {
  const [status, setStatus] = useState<"assigning" | "connected" | "waiting" | "received" | "error" | "timeout">("assigning");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [deviceId, setDeviceId] = useState<string | null>(propDeviceId || null);
  const [countdownSeconds, setCountdownSeconds] = useState(READING_TIMEOUT_SECONDS);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const connectedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const readingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef<boolean>(false);
  const hasDisassociatedRef = useRef<boolean>(false);
  const isClosingRef = useRef<boolean>(false);
  const deviceIdRef = useRef<string | null>(propDeviceId || null);

  const updateDeviceId = (nextDeviceId: string | null) => {
    deviceIdRef.current = nextDeviceId;
    setDeviceId(nextDeviceId);
  };

  const clearCountdownTimers = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (readingTimeoutRef.current) {
      clearTimeout(readingTimeoutRef.current);
      readingTimeoutRef.current = null;
    }
  };

  // Update deviceId when prop changes
  useEffect(() => {
    if (propDeviceId) {
      updateDeviceId(propDeviceId);
    }
  }, [propDeviceId]);

  // Assign device when popup opens
  useEffect(() => {
    if (isOpen && patientId) {
      assignDevice();
    } else if (!isOpen) {
      // Reset state when popup closes
      setStatus("assigning");
      setErrorMessage("");
      setCountdownSeconds(READING_TIMEOUT_SECONDS);
      if (!propDeviceId) {
        updateDeviceId(null);
      }
      hasDisassociatedRef.current = false;
      isClosingRef.current = false;
    }

    // Cleanup on unmount or close
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (connectedTimeoutRef.current) {
        clearTimeout(connectedTimeoutRef.current);
      }
      clearCountdownTimers();
      isPollingRef.current = false;
    };
  }, [isOpen, patientId]);

  const assignDevice = async () => {
    try {
      setStatus("assigning");
      setErrorMessage("");
      setCountdownSeconds(READING_TIMEOUT_SECONDS);

      const payload: Record<string, unknown> = {
        patientId,
        device_type: deviceType,
      };

      // Admin flow: reading is assigned from the selected caretaker's device.
      if (caretakerId) {
        payload.caretakerId = caretakerId;
      }

      const response = await AxiosInstance().post("/reading/assign-devices-from-caretaker", payload);

      // Try to get deviceId from response
      const responseData = (response.data as any)?.data || (response.data as any);
      const assignedDeviceId = responseData?.deviceId || responseData?.device?._id;

      if (assignedDeviceId) {
        updateDeviceId(assignedDeviceId);
      } else if (!propDeviceId) {
        // Fetch patient devices to find the deviceId only if not provided as prop
        await fetchDeviceId();
      } else {
        // Use prop deviceId if available
        updateDeviceId(propDeviceId);
      }

      // Show connected message for 5 seconds before starting to wait
      setStatus("connected");

      // After 5 seconds, transition to waiting and start polling + 4 min countdown
      connectedTimeoutRef.current = setTimeout(() => {
        setStatus("waiting");
        startPolling();
        startReadingCountdown();
      }, 5000);
    } catch (error: any) {
      console.error("Error assigning device:", error);
      setStatus("error");
      setErrorMessage(
        error?.response?.data?.message || "Failed to assign device. Please try again."
      );
      toast.error("Failed to assign device");
    }
  };

  const fetchDeviceId = async () => {
    try {
      const response = await AxiosInstance().get(`/devices?patientId=${patientId}`);
      const devices = ((response.data as any)?.data?.devices) || [];

      // Find device matching the deviceType
      const matchingDevice = devices.find((device: any) => {
        const deviceTypeMap: Record<string, string> = {
          blood_pressure: "blood_pressure",
          glucose_meter: "glucose_meter",
          pulse_oximeter: "pulse_oximeter",
          weight_scale: "weight_scale",
        };
        return device.deviceType === deviceTypeMap[deviceType] ||
          device.deviceName?.toLowerCase().includes(deviceType.replace(/_/g, " "));
      });

      if (matchingDevice?._id) {
        updateDeviceId(matchingDevice._id);
        return matchingDevice._id;
      }
    } catch (error) {
      console.error("Error fetching deviceId:", error);
      // Continue without deviceId - disassociate will be skipped if deviceId is not available
    }
    return null;
  };

  const handleReadingTimeout = () => {
    if (isClosingRef.current) return;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
    clearCountdownTimers();

    // Keep the device assigned and show the timeout screen. Retry restarts the
    // timer only; disassociation happens when the timeout popup is closed.
    setStatus("timeout");
  };

  const handleTimeoutRetry = () => {
    // Device is still assigned, so just restart the timer + polling (no re-assign).
    setStatus("waiting");
    startPolling();
    startReadingCountdown();
  };

  const handleRetry = () => {
    // Error state: device was never assigned, so run the full assign flow again.
    hasDisassociatedRef.current = false;
    isClosingRef.current = false;
    void assignDevice();
  };

  const startReadingCountdown = () => {
    clearCountdownTimers();
    setCountdownSeconds(READING_TIMEOUT_SECONDS);

    countdownIntervalRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    readingTimeoutRef.current = setTimeout(() => {
      void handleReadingTimeout();
    }, READING_TIMEOUT_SECONDS * 1000);
  };

  const startPolling = () => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    let pollCount = 0;
    const maxPolls = Math.ceil((READING_TIMEOUT_SECONDS * 1000) / POLL_INTERVAL_MS);

    // Poll every 3 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        pollCount++;

        // Stop polling after max attempts (aligned with 4 min timeout)
        if (pollCount > maxPolls) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          isPollingRef.current = false;
          return;
        }

        const response = await AxiosInstance().get(
          `/reading/check-patient-readings?patientId=${patientId}&device_type=${deviceType}`
        );

        // Check if new reading has arrived
        const responseData = (response.data as any)?.data || (response.data as any);
        const newReadingsArrived = responseData?.newReadingsArrived || false;

        if (newReadingsArrived) {
          setStatus("received");
          clearCountdownTimers();
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          isPollingRef.current = false;

          // If deviceId is not set yet, try to fetch it
          if (!deviceIdRef.current) {
            await fetchDeviceId();
          }
        }
      } catch (error: any) {
        console.error("Error checking readings:", error);
        // Don't stop polling on error, just log it
      }
    }, POLL_INTERVAL_MS);
  };

  const disassociateDevice = async () => {
    // Prevent duplicate calls
    if (hasDisassociatedRef.current) {
      return;
    }

    // Resolve the deviceId one more time if it was never captured (e.g. outpatient
    // flow where assign didn't return an id) so the call isn't silently skipped.
    let activeDeviceId = deviceIdRef.current || deviceId;
    if (!activeDeviceId) {
      activeDeviceId = await fetchDeviceId();
    }

    if (!activeDeviceId) {
      console.warn("DeviceId not available, skipping disassociate");
      return;
    }

    hasDisassociatedRef.current = true;

    try {
      await AxiosInstance().post("/devices/disassociate", {
        deviceId: activeDeviceId,
      });
      console.log("Device disassociated successfully");
    } catch (error: any) {
      console.error("Error disassociating device:", error);
      // Don't show error to user, just log it
      hasDisassociatedRef.current = false; // Reset on error so it can be retried
    }
  };

  const cleanup = () => {
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    // Clear connected timeout
    if (connectedTimeoutRef.current) {
      clearTimeout(connectedTimeoutRef.current);
      connectedTimeoutRef.current = null;
    }
    clearCountdownTimers();
    isPollingRef.current = false;

    // Reset state
    setStatus("assigning");
    setErrorMessage("");
    setCountdownSeconds(READING_TIMEOUT_SECONDS);
    if (!propDeviceId) {
      updateDeviceId(null);
    }
  };

  const handleManualClose = async () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;

    // Disassociate when the user manually closes after assignment.
    await disassociateDevice();

    if (status === "received") {
      onReadingReceived?.();
    }

    cleanup();
    onClose();
  };

  const handleDialogChange = async (open: boolean) => {
    if (!open) {
      await handleManualClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent
        className="max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(event) => {
          event.preventDefault();
          void handleManualClose();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Take Reading</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleManualClose}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center py-8 space-y-6">
          {status === "assigning" && (
            <>
              <ConnectingPlugsIcon />
              <div className="text-center">
                <p className="text-lg font-semibold text-tertiary">
                  Assigning Device...
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Please wait while we connect to your device
                </p>
              </div>
            </>
          )}

          {status === "connected" && (
            <>
              <ConnectingPlugsIcon attachPlug />
              <div className="text-center space-y-2">
                <p className="text-lg font-semibold text-tertiary">
                  Ensure the device is powered on and ready for use.
                </p>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Confirm the device is properly attached to the patient before initiating the reading.</p>
                </div>
              </div>
            </>
          )}

          {status === "waiting" && (
            <>
              <div className="relative">
                <div className="h-20 w-20 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-16 w-16 bg-primary/5 rounded-full animate-ping"></div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-tertiary">
                  Waiting for Reading...
                </p>
                <p className="mt-3 text-3xl font-bold tabular-nums tracking-wide text-primary">
                  {formatCountdown(countdownSeconds)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Please take your reading now. If no reading is received within 4 minutes, you can retry.
                </p>
                <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-2 pt-1 text-sm text-red-600">
                  <Info className="h-4 w-4 shrink-0" />
                  <span>Do not close this popup or you might lose the reading.</span>
                </div>
              </div>
            </>
          )}

          {status === "received" && (
            <>
              <div className="relative">
                <CheckCircle2 className="h-20 w-20 text-green-500 animate-in zoom-in duration-300" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-16 w-16 bg-green-500/20 rounded-full animate-ping"></div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-green-600">
                  Reading taken successful
                </p>
              </div>
              <Button onClick={handleManualClose} className="mt-4">
                Close
              </Button>
            </>
          )}

          {status === "timeout" && (
            <>
              <div className="relative">
                <TimerOff className="h-20 w-20 text-destructive" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-destructive">
                  Reading Timed Out
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  No reading was received within 4 minutes. Please make sure the
                  device is powered on and attached to the patient, then try again.
                </p>
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={handleManualClose}>
                  Close
                </Button>
                <Button onClick={handleTimeoutRetry}>
                  Retry
                </Button>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="relative">
                <X className="h-20 w-20 text-destructive" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-destructive">
                  Error
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {errorMessage || "Something went wrong. Please try again."}
                </p>
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={handleManualClose}>
                  Close
                </Button>
                <Button onClick={handleRetry}>
                  Retry
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TakeReadingPopup;
